import React, { useEffect, useState } from 'react';

// Reads dynamic site branding from window.__vaSettings (populated by <SiteHead />).
// Re-renders when SiteHead dispatches 'va-settings-updated'.
export const useSiteBrand = () => {
  const [brand, setBrand] = useState(() => (typeof window !== 'undefined' ? (window.__vaSettings || {}) : {}));
  useEffect(() => {
    const onUpd = (e) => setBrand(e.detail || {});
    window.addEventListener('va-settings-updated', onUpd);
    if (typeof window !== 'undefined' && window.__vaSettings) setBrand(window.__vaSettings);
    return () => window.removeEventListener('va-settings-updated', onUpd);
  }, []);
  return brand;
};

// Reusable brand logo — falls back to a gradient icon + site_name wordmark
// when no custom logo is configured. Sizes:
//   size = 'sm' (header compact) | 'md' (header) | 'lg' (login/full-screen)
export const BrandLogo = ({ size = 'md', showWordmark = true, className = '' }) => {
  const brand = useSiteBrand();
  const rawName = brand.site_name || 'NUBES MTY';
  const parts = rawName.trim().split(/\s+/);
  const primary = parts[0] || 'SITIO';
  const secondary = parts.slice(1).join(' ');

  const sizes = {
    sm: { box: 'w-8 h-8', svg: 'w-4 h-4', pW: 'text-base', pS: 'text-[9px] tracking-[0.32em]', imgH: 32, glow: 24, blur: 18 },
    md: { box: 'w-11 h-11', svg: 'w-6 h-6', pW: 'text-lg', pS: 'text-[10px] tracking-[0.35em]', imgH: 44, glow: 36, blur: 26 },
    lg: { box: 'w-40 h-40 rounded-3xl', svg: 'w-24 h-24', pW: 'text-4xl', pS: 'text-sm tracking-[0.5em]', imgH: 160, glow: 120, blur: 70 },
  };
  const cfg = sizes[size] || sizes.md;
  const customH = Number(brand.logo_height);
  const imgH = size === 'md' && customH > 0 ? Math.min(customH, 96) : cfg.imgH;

  if (brand.logo_url) {
    return (
      <div className={`relative inline-flex items-center justify-center group ${className}`}>
        {/* Soft radial glow behind — gives the mark presence without a hard box */}
        <span
          aria-hidden
          className="absolute inset-0 -z-10 pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(closest-side, rgba(249,115,22,0.28), rgba(239,68,68,0.10) 55%, transparent 75%)`,
            filter: `blur(${cfg.blur}px)`,
            transform: 'scale(1.35)',
          }}
        />
        <img
          src={brand.logo_url}
          alt={rawName}
          style={{
            height: imgH,
            filter: 'drop-shadow(0 6px 14px rgba(249,115,22,0.28)) drop-shadow(0 2px 4px rgba(0,0,0,0.35))',
          }}
          className="w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 ${size === 'lg' ? 'flex-col gap-4' : ''} ${className}`}>
      <div className={`${cfg.box} rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/25 ${size === 'lg' ? 'shadow-2xl shadow-orange-500/40' : ''}`}>
        <svg viewBox="0 0 24 24" fill="none" className={`${cfg.svg} text-white`}>
          <path d="M6 3v6a6 6 0 1 0 12 0V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M12 15v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      {showWordmark && (
        <div className="flex flex-col leading-none items-center">
          <span className={`text-white font-black tracking-tight uppercase ${cfg.pW}`}>{primary}</span>
          {secondary && <span className={`text-orange-500 font-extrabold uppercase mt-0.5 ${cfg.pS}`}>{secondary}</span>}
        </div>
      )}
    </div>
  );
};

export default BrandLogo;
